import { useEffect, useState } from 'react';
import { useBodyClass } from '@/hooks/useBodyClass';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Send, MessageCircle, Image as ImageIcon, Check, Lock } from 'lucide-react';
import { getWeekRation, markWeekRationSeen, STREAK_GOAL, type WeekRation } from '@/lib/weekRation';
import { getNoteForTrigger, evidenceLabel, RESEARCH_DISCLAIMER, type ResearchNote } from '@/lib/researchNotes';
import { shareAchievement, canShareToStory, type ShareChannel } from '@/utils/achievementCard';
import { weekDofiLine, weekShareRows } from '@/utils/weekCard';
import { ShareButton } from '@/components/AchievementUnlockModal';
import { useUserStore } from '@/store/userStore';
import { Mascot } from '@/components/Mascot';
import { LEVELS, MAX_LEVEL, getWeekExtras, type WeekExtras } from '@/lib/weekLevels';
import { RhythmSection, SourcesSection, DynamicsSection, LockedLevel } from '@/components/WeekLevelSections';

/**
 * Разбор рациона за неделю (29.09.2026, замысел Славы).
 *
 * До пяти дней подряд карточка показывает ОБЕЩАНИЕ и прогресс — это и есть
 * анонс функции. Всплывающего окна нет намеренно: правило 1 хартии
 * ненавязчивости, а обещание всё равно видно там, куда человек и так смотрит.
 *
 * После пяти дней открывается разбор. Модалка тут уместна: она не перекрывает
 * действие, а награждает за него.
 *
 * ⚠️ Что уходит наружу и что остаётся внутри (решение Славы 29.09.2026):
 * делятся гордостью, а не дефицитом. На карточку для сторис — серия, число
 * приёмов и радуга; провалы по нутриентам видны только в приложении.
 */

const DOTS = Array.from({ length: STREAK_GOAL });

export function RationWeekCard() {
  const [data, setData] = useState<WeekRation | null>(null);
  const [open, setOpen] = useState(false);
  const { profile } = useUserStore();

  useEffect(() => {
    getWeekRation().then(setData);
  }, []);

  // Закрыл разбор — круг засчитан, карточка переходит к следующему (0 из 5)
  async function closeReview() {
    setOpen(false);
    if (data?.unlocked && profile?.id) {
      await markWeekRationSeen();
      getWeekRation().then(setData);
    }
  }

  // Пока человек не записал ни одного дня — молчим: у новичка уже есть цель
  // (первая запись), а вторая в тот же момент делит внимание.
  if (!data || data.daysFull === 0) return null;

  const left = STREAK_GOAL - Math.min(data.cycle, STREAK_GOAL);
  const days = (n: number) => (n === 1 ? 'день' : n < 5 ? 'дня' : 'дней');
  // Что откроет идущий круг. После четвёртого новых блоков нет — растёт динамика.
  const next = data.circle <= MAX_LEVEL ? LEVELS[data.circle - 1] : null;

  let title: string;
  let text: string;
  if (data.unlocked) {
    title = data.circle === 1 ? 'Разбор рациона готов' : `Разбор готов · круг ${data.circle}`;
    text = data.circle === 1
      ? `${data.streak} дней подряд, ${data.meals} приёмов. Я собрал, что из этого вышло.`
      : next
        ? `Открылось новое — ${next.title.toLowerCase()}: ${next.promise}.`
        : 'Свежая неделя и новая строка в динамике: было → стало.';
  } else if (data.circle === 1) {
    title = 'Разберу твой рацион целиком';
    text = `Записывай ${STREAK_GOAL} дней подряд — и я покажу витамины, минералы и что об этом говорят исследования.`;
  } else {
    title = next ? `Круг ${data.circle}: ${next.title.toLowerCase()}` : `Круг ${data.circle}: новая неделя`;
    text = next
      ? `Ещё ${left} ${days(left)} подряд с дневником — и покажу, ${next.promise}.`
      : `Ещё ${left} ${days(left)} подряд — и добавлю новую строку в динамику.`;
  }

  return (
    <>
      <div className="rounded-2xl border border-line bg-bg-card p-4">
        <div className="flex items-start gap-3">
          <Mascot pose={data.unlocked ? 'celebrate' : 'think'} compact className="w-20 h-20 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm">{title}</div>
            <p className="text-xs text-ink-dim mt-0.5">{text}</p>

            <div className="flex items-center gap-1.5 mt-2.5">
              {DOTS.map((_, i) => (
                <span
                  key={i}
                  className={`h-2 flex-1 rounded-full ${
                    i < data.cycle ? 'bg-emerald-glow' : 'bg-bg-elevated'
                  }`}
                />
              ))}
              <span className="text-[11px] text-ink-muted tabular-nums ml-1 whitespace-nowrap">
                {Math.min(data.cycle, STREAK_GOAL)} / {STREAK_GOAL}
              </span>
            </div>

            {data.unlocked && (
              <button
                onClick={() => setOpen(true)}
                className="w-full mt-3 py-2 rounded-lg text-xs font-medium text-emerald-glow bg-emerald-glow/10 border border-emerald-glow/20"
              >
                Открыть разбор
              </button>
            )}
          </div>
        </div>

        {/* Лестница кругов: что уже открыто, что идёт сейчас, что впереди.
            Показываем с первого просмотренного разбора — новичку хватает одной цели.
            Отдельной строкой во всю ширину: рядом с Дофи четыре круга не помещались. */}
        {data.reviews > 0 && (
          <div className="grid grid-cols-4 gap-1 mt-3.5 pt-3 border-t border-line/50">
            {LEVELS.map((l) => {
              const done = l.n <= data.reviews;
              const current = l.n === data.circle;
              return (
                <div key={l.n} className={`flex flex-col items-center gap-1 ${done || current ? '' : 'opacity-45'}`}>
                  <div
                    className={`relative w-9 h-9 rounded-full flex items-center justify-center text-base border ${
                      done
                        ? 'bg-emerald-glow/15 border-emerald-glow/50'
                        : current
                          ? 'bg-bg-elevated border-emerald-glow'
                          : 'bg-bg-elevated border-line'
                    }`}
                  >
                    <span className={done || current ? '' : 'grayscale'}>{l.emoji}</span>
                    <span
                      className={`absolute -right-1 -bottom-1 w-4 h-4 rounded-full flex items-center justify-center ${
                        done ? 'bg-emerald-glow text-bg' : 'bg-bg-card text-ink-dim border border-line'
                      }`}
                    >
                      {done ? <Check size={10} strokeWidth={3.5} /> : current ? <span className="text-[9px] font-bold text-emerald-glow">{l.n}</span> : <Lock size={9} />}
                    </span>
                  </div>
                  <span className={`text-[10px] leading-tight text-center ${current ? 'text-emerald-glow font-medium' : 'text-ink-dim'}`}>
                    {l.short}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <RationWeekModal open={open} onClose={closeReview} data={data} />
    </>
  );
}

function RationWeekModal({
  open, onClose, data,
}: { open: boolean; onClose: () => void; data: WeekRation }) {
  // Экраны лежат в слое ниже нижней панели (z-10 против z-30): без этого панель
  // закрывает низ выезжающего окна вместе с его кнопками (боевой случай 10.10.2026).
  useBodyClass('modal-open', open);
  const { profile } = useUserStore();
  const [note, setNote] = useState<ResearchNote | null>(null);
  const [busy, setBusy] = useState<ShareChannel | null>(null);
  const [extras, setExtras] = useState<WeekExtras | null>(null);
  // Какие блоки открыты в этом разборе. После четвёртого круга — все.
  const level = Math.min(data.circle, MAX_LEVEL);
  // Блок, ради которого этот круг и проходили, идёт первым
  const fresh = data.circle >= 2 && data.circle <= MAX_LEVEL ? data.circle : null;

  useEffect(() => {
    if (!open) return;
    getNoteForTrigger('streak_5').then(setNote);
    if (level >= 2) getWeekExtras(data).then(setExtras);
  }, [open]);

  function extra(n: number, badge?: string) {
    if (!extras || n > level) return null;
    if (n === 2 && extras.rhythm) return <RhythmSection key={2} r={extras.rhythm} badge={badge} />;
    if (n === 3 && extras.sources) return <SourcesSection key={3} s={extras.sources} badge={badge} />;
    if (n === 4 && extras.dynamics) return <DynamicsSection key={4} d={extras.dynamics} badge={badge} />;
    return null;
  }

  // Наружу уходит сам разбор: Дофи, сильные нутриенты, радуга (weekCard.ts).
  // Три канала, как у достижений; раньше была одна кнопка «в историю».
  async function share(channel: ShareChannel) {
    if (!profile?.id) return;
    setBusy(channel);
    try {
      await shareAchievement(channel, {
        id: 'ration_week',
        title: 'Моя неделя в дневнике',
        icon: '🌿',
        slava_line: weekDofiLine(data, weekShareRows(data)),
        target_value: STREAK_GOAL,
        category: 'progress',
      }, { userId: profile.id, name: profile.first_name, refCode: (profile as any).ref_code, week: data });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[92vh] overflow-y-auto bg-bg-card rounded-t-3xl border-t border-line max-w-md mx-auto"
          >
            <div className="sticky top-0 bg-bg-card border-b border-line/40 px-5 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-emerald-glow" />
                <div>
                  <div className="font-display font-bold text-base leading-tight">
                    Твоя неделя{data.circle > 1 ? ` · круг ${data.circle}` : ''}
                  </div>
                  <div className="text-xs text-ink-dim">
                    {data.daysFull} дней · {data.meals} приёмов · ~{data.perDayCalories} ккал в день
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-bg-elevated text-ink-muted flex items-center justify-center shrink-0"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-5 pb-8 pt-4 space-y-5">
              {/* Новое в этом круге — сверху: ради него серию и проходили.
                  После четвёртого круга первой идёт динамика — она меняется каждую неделю. */}
              {fresh ? extra(fresh, 'новое в этом круге') : data.circle > MAX_LEVEL ? extra(4, 'обновилось') : null}

              {/* Радуга — то, ради чего разбор показывают друзьям.
                  Здесь только собранное, ни одного провала. */}
              <section>
                <div className="font-display font-bold text-sm mb-1">
                  Радуга недели: {data.rainbow.length} из 7 цветов
                </div>
                <p className="text-xs text-ink-dim mb-3">
                  Разных растений за неделю: {data.plants}. Чем шире палитра, тем разнообразнее
                  то, что получает микробиом.
                </p>
                <div className="space-y-1.5">
                  {data.rainbow.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.dot }} />
                      <span className="font-medium w-24 shrink-0">{c.label}</span>
                      <span className="text-ink-dim truncate">{c.examples.join(', ')}</span>
                    </div>
                  ))}
                </div>
                {data.rainbowMissing.length > 0 && (
                  <p className="text-[11px] text-ink-muted mt-2.5">
                    Не хватает: {data.rainbowMissing.map((c) => c.label.toLowerCase()).join(', ')}.
                    {data.rainbowMissing.some((c) => c.id === 'purple') && ' Фиолетовый — это баклажан, черника, слива.'}
                  </p>
                )}
              </section>

              {/* Сильные стороны */}
              {data.strong.length > 0 && (
                <section>
                  <div className="font-display font-bold text-sm mb-2">Чем неделя сильна</div>
                  <div className="space-y-2">
                    {data.strong.map((r) => (
                      <div key={r.key} className="flex items-baseline justify-between gap-2 text-xs">
                        <span>{r.emoji} {r.label}</span>
                        <span className="text-ink-dim tabular-nums whitespace-nowrap">
                          ~{r.perDay} {r.unit} в день · {r.percent}%
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Провалы — только внутри приложения, на карточку не уходят */}
              {data.weak.length > 0 && (
                <section>
                  <div className="font-display font-bold text-sm mb-2">На что посмотреть</div>
                  <div className="space-y-2">
                    {data.weak.map((r) => (
                      <div key={r.key} className="flex items-baseline justify-between gap-2 text-xs">
                        <span>{r.emoji} {r.label}</span>
                        <span className="text-ink-dim tabular-nums whitespace-nowrap">
                          ~{r.perDay} {r.unit} в день · {r.percent}%
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-ink-muted mt-2">
                    Это не повод волноваться: норма набирается не за день, а за недели,
                    и почти всё закрывается обычной едой.
                  </p>
                </section>
              )}

              {/* Остальные открытые блоки — те, что получены в прошлых кругах */}
              {[2, 3, 4].filter((n) => n !== fresh && !(data.circle > MAX_LEVEL && n === 4)).map((n) => extra(n))}

              {/* Что откроет следующая серия */}
              {data.circle < MAX_LEVEL ? (
                <LockedLevel n={data.circle + 1} />
              ) : (
                <p className="text-xs text-ink-muted text-center px-3">
                  Все четыре блока открыты. Каждая следующая серия — свежая неделя и новая строка в динамике.
                </p>
              )}

              {note && (
                <section className="rounded-xl bg-bg-elevated/60 p-3.5">
                  <div className="text-[11px] uppercase tracking-wider text-emerald-glow mb-1.5">
                    Что говорят учёные
                  </div>
                  <p className="text-sm leading-snug">{note.claim}</p>
                  <p className="text-xs leading-relaxed text-ink-dim mt-1.5">{note.detail}</p>
                  <a
                    href={note.url} target="_blank" rel="noreferrer"
                    className="inline-block text-[11px] text-emerald-glow mt-2"
                  >
                    {note.authors}, {note.journal}, {note.year}
                  </a>
                  <div className="text-[11px] text-ink-muted">{evidenceLabel(note)}</div>
                </section>
              )}

              <div>
                <div className="text-[11px] text-ink-dim text-center mb-2">
                  На карточку попадут Дофи, сильные стороны недели и радуга — без провалов
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {canShareToStory() && (
                    <ShareButton Icon={Send} label="В историю" busy={busy === 'story'} disabled={!!busy} onClick={() => share('story')} accent />
                  )}
                  <ShareButton Icon={MessageCircle} label="Другу" busy={busy === 'chat'} disabled={!!busy} onClick={() => share('chat')} />
                  <ShareButton Icon={ImageIcon} label="Картинкой" busy={busy === 'image'} disabled={!!busy} onClick={() => share('image')} />
                </div>
              </div>

              {data.pendingItems > 0 && (
                <p className="text-[11px] text-ink-muted">
                  {data.pendingItems} продуктов ещё досчитываются — цифры могут немного подрасти.
                </p>
              )}
              <p className="text-[11px] leading-snug text-ink-muted">{RESEARCH_DISCLAIMER}</p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
